// @flow
import { STATUS_PENDING } from 'box-ui-elements/es/constants';
import {
  getFileId,
  getFile,
  getFileAPIOptions,
} from 'box-ui-elements/es/utils/uploads';
import filesize from 'filesize';
import type {
  UploadItem,
  UploadFile,
  UploadFileWithAPIOptions,
} from 'box-ui-elements/es/common/types/upload';

class ItemQueue {
  elementId: string;

  rootFolderId: string;

  itemIds: Object;

  items: UploadItem[];

  errorCode: string;

  constructor(elementId: string, rootFolderId: string) {
    this.elementId = elementId;
    this.rootFolderId = rootFolderId;
    this.itemIds = {};
    this.items = [];
    this.errorCode = '';
  }

  /**
   * Given an array of files, return the files that are new to the Content Uploader
   *
   * @param {Array<UploadFileWithAPIOptions | File>} files
   */
  getNewFiles(
    files: Array<UploadFileWithAPIOptions | File>,
  ): Array<UploadFileWithAPIOptions | File> {
    const { rootFolderId, itemIds } = this;

    return Array.from(files).filter(
      file => !itemIds[getFileId(file, rootFolderId)],
    );
  }

  /**
   * Converts File API to upload items and adds to upload queue.
   *
   * @private
   * @param {Array<UploadFileWithAPIOptions | UploadFile>} files - Files to be added to upload queue
   * @return {void}
   */
  addFilesToUploadQueue(files?: Array<UploadFileWithAPIOptions | UploadFile>) {
    const { rootFolderId } = this;
    if (!files || files.length === 0) {
      return;
    }

    const newFiles = this.getNewFiles(files);

    if (newFiles.length === 0) {
      return;
    }

    const newItemIds = {};

    newFiles.forEach(file => {
      newItemIds[getFileId(file, rootFolderId)] = true;
    });

    this.itemIds = {
      ...this.itemIds,
      ...newItemIds,
    };

    this.addFilesWithoutRelativePathToQueue(newFiles);
  }

  /**
   * Converts File API to upload items and adds to upload queue for files with webkitRelativePath missing or ignored.
   *
   * @private
   * @param {Array<UploadFileWithAPIOptions | File>} files - Files to be added to upload queue
   * @return {void}
   */
  addFilesWithoutRelativePathToQueue(
    files: Array<UploadFileWithAPIOptions | File>,
  ) {
    // Convert files from the file API to upload items
    const newItems = files.map(file => {
      const uploadFile = getFile(file);
      const { name, size } = uploadFile;

      // Extract extension or use empty string if file has no extension
      let extension = name.substr(name.lastIndexOf('.') + 1);
      if (extension.length === name.length) {
        extension = '';
      }

      const uploadItem: Object = {
        elementId: this.elementId,
        extension,
        file: uploadFile,
        name,
        progress: 0,
        size,
        status: STATUS_PENDING,
        fileSize: filesize(size, { round: 1 }),
      };

      uploadItem.options = getFileAPIOptions(file);

      return uploadItem;
    });

    this.addToQueue(newItems);
  }

  /**
   * Add new items to the upload queue
   *
   * @private
   * @param {Array<UploadFileWithAPIOptions | File>} newItems - Files to be added to upload queue
   * @return {void}
   */
  addToQueue(newItems: UploadItem[]) {
    const { items } = this;
    this.items = items.concat(newItems);
  }

  /**
   * Removes an item from the upload queue. Cancels upload if in progress.
   *
   * @param {UploadItem} item - Item to remove
   * @return {void}
   */
  removeItem(item: UploadItem) {
    const { rootFolderId, items, itemIds } = this;
    items.splice(items.indexOf(item), 1);
    this.items = items.concat([]);
    delete itemIds[getFileId(item.file, rootFolderId)];
  }
}

export { ItemQueue };
export default ItemQueue;
